import {Box, FormControlLabel, Switch, Tooltip, Typography, useTheme} from '@mui/material';
import {Info} from "lucide-react"
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from 'react-oidc-context';
import {useDispatch} from 'react-redux';
import {editUser} from '../../actions/admin';
import {mapUser} from '../../utils/mapUser';

const EmailReportToggle = () => {
    const { t } = useTranslation();
    const auth = useAuth();
    const user = mapUser(auth);
    const dispatch = useDispatch();
    const theme = useTheme();

    const [isUpdating, setIsUpdating] = useState(false);

    const convertToBoolean = (value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') return value.toLowerCase() === 'true';
        return false;
    };

    const [isReportingEnabled, setIsReportingEnabled] = useState(() => {
        return convertToBoolean(user?.emailReporting);
    });

    useEffect(() => {
        if (user?.emailReporting !== undefined && !isUpdating) {
            const booleanValue = convertToBoolean(user.emailReporting);
            setIsReportingEnabled(booleanValue);
        }
    }, [user?.emailReporting, user?.id, isUpdating]);

    const handleToggle = async (event) => {
        event.stopPropagation();
        const newToggleState = event.target.checked;

        setIsUpdating(true);
        setIsReportingEnabled(newToggleState);

        try {
            await updateUser({
                ...user,
                emailReporting: newToggleState.toString()
            });

            await new Promise(resolve => setTimeout(resolve, 500));
            await refreshUserToken();

        } catch (error) {
            console.error('Error updating admin or refreshing token:', error);
            setIsReportingEnabled(prev => !prev);
        } finally {
            setIsUpdating(false);
        }
    };

    const updateUser = async (data) => {
        const dataForEdit = {
            id: data.id,
            emailReporting: data.emailReporting
        };

        await dispatch(editUser(dataForEdit));
    };

    const refreshUserToken = async () => {
        try {
            await auth.signinSilent();
            await new Promise(resolve => setTimeout(resolve, 300));

            const refreshedUser = mapUser(auth);

            if (!refreshedUser || refreshedUser.emailReporting === undefined) {
                await auth.removeUser();
                await auth.signinRedirect();
            }

        } catch (error) {
            console.error('Error refreshing token:', error);

            try {
                await auth.removeUser();
                await auth.signinRedirect();
            } catch (fallbackError) {
                console.error('Fallback authentication failed:', fallbackError);
                throw fallbackError;
            }
        }
    };

    return (
        <FormControlLabel
            sx={{ mt:1, mr: 1, mb: 0, ml: 0 }}
            control={
                <Switch
                    checked={isReportingEnabled}
                    onChange={handleToggle}
                    size="small"
                    disabled={isUpdating}
                />
            }
            label={
                <Box sx={{ display: 'flex', alignItems: 'top', gap: 0.5 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontFamily: "Gilroy-Regular", fontWeight: "bold", textWrap: "nowrap" }}
                    >
                        {t("Reports.Dashboard.Email")}:
                    </Typography>
                    <Tooltip title={t("Reports.Dashboard.EmailDescription")}>
                        <Info color={theme.palette.primary.main} size={16} />
                    </Tooltip>
                </Box>
            }
            labelPlacement="start"
        />
    );
};

export default EmailReportToggle;
